import {
  CnpjFormField,
  CpfFormField,
  FloatingFormItem,
  InputDecimal,
} from '@/components/_common';
import RadioGroup from '@/components/_common/RadioGroup';
import {
  useCreateDeduction,
  useDeduction,
  useUpdateDeduction,
} from '@/core/domain/invoice/Deduction/Deduction.hook';
import {
  CreateDeduction,
  DeductionFormValues,
  UpdateDeduction,
} from '@/core/domain/invoice/Deduction/Deduction.types';
import { PersonType } from '@/core/domain/people/Customers/Customers.types';
import { removeMask } from '@/utils/mask';

import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  ModalProps,
  Radio,
  Row,
  Select,
} from 'antd';
import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

interface Props extends Omit<ModalProps, 'onCancel'> {
  deductionId?: number;
  invoiceId: number;
  onCancel: () => void;
}

const NfsDeductionModalForm: React.FC<Props> = ({
  open,
  onCancel,
  deductionId,
  invoiceId,
}) => {
  const intl = useIntl();

  const [form] = Form.useForm<DeductionFormValues>();

  const createMutation = useCreateDeduction();
  const updateMutation = useUpdateDeduction();
  const { data: detailDeduction, mutateAsync } = useDeduction();

  const personTypeWatch = Form.useWatch('personType', form);
  const formValue = Form.useWatch('form', form);

  useEffect(() => {
    if (deductionId) {
      mutateAsync({
        id: deductionId,
        invoiceId,
      });
    }
  }, [deductionId, open]);

  useEffect(() => {
    if (detailDeduction) {
      form.setFieldsValue({
        ...detailDeduction,
        personType:
          Number(detailDeduction.cpfCnpjReference) === 11
            ? PersonType.Natural
            : PersonType.Legal,
      });
    }
  }, [detailDeduction]);

  const optionsFormValue = [
    {
      value: 1,
      label: 'Percentual',
    },
    {
      value: 2,
      label: 'Valor',
    },
  ];

  const optionsType = [
    {
      value: 1,
      label: 'Despesas com Materiais',
    },
    {
      value: 2,
      label: 'Despesas com Sub-empreitada',
    },
  ];

  const handleCancel = () => {
    form.resetFields();

    onCancel();
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();

    delete values.personType;

    if (deductionId) {
      const obj: UpdateDeduction = {
        ...values,
        cpfCnpjReference: removeMask(values.cpfCnpjReference),
        invoiceId,
        id: deductionId,
      };

      await updateMutation.mutateAsync(obj);

      handleCancel();
    } else {
      const obj: CreateDeduction = {
        ...values,
        cpfCnpjReference: removeMask(values.cpfCnpjReference),
        invoiceId,
      };

      await createMutation.mutateAsync(obj);

      handleCancel();
    }
  };

  return (
    <Modal
      title={`${!!deductionId ? 'Edição' : 'Criação'} da Dedução`}
      open={open}
      onCancel={handleCancel}
      width={400}
      footer={[
        <Button type="primary" ghost onClick={handleCancel}>
          {intl.formatMessage({ id: 'commons.modal.button.cancel' })}
        </Button>,
        <Button type="primary" onClick={handleSubmit}>
          {deductionId ? 'Editar' : 'Criar'}
        </Button>,
      ]}
      destroyOnClose
    >
      <Form form={form}>
        <Row gutter={[12, 12]}>
          <Col span={24}>
            <FloatingFormItem
              name="form"
              label="Forma de dedução"
              initialValue={2}
            >
              <Select options={optionsFormValue} />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <FloatingFormItem name="type" label="Tipo de dedução">
              <Select options={optionsType} />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <Form.Item noStyle name="personType" initialValue="NATURAL">
              <RadioGroup style={{ marginBottom: '8px' }}>
                <Radio value="NATURAL">
                  <FormattedMessage id="customers.domain.personType.natural" />
                </Radio>
                <Radio value="LEGAL">
                  <FormattedMessage id="customers.domain.personType.legal" />
                </Radio>
              </RadioGroup>
            </Form.Item>
          </Col>

          <Col span={24}>
            {personTypeWatch === 'NATURAL' ? (
              <CpfFormField fieldName="cpfCnpjReference" />
            ) : (
              <CnpjFormField fieldName="cpfCnpjReference" />
            )}
          </Col>

          {formValue === 1 ? (
            <Col span={24}>
              <FloatingFormItem
                name="deductionPercentage"
                label="Deduzir (%)"
                initialValue={0}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>
          ) : (
            <Col span={24}>
              <FloatingFormItem
                name="deductionValue"
                label="Deduzir (R$)"
                initialValue={0}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>
          )}

          <Col span={24}>
            <FloatingFormItem
              name="referenceNfNumber"
              label="Número da Nota Fiscal de Referência"
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <FloatingFormItem
              name="referenceTotalValue"
              label="Valor total da Nota Fiscal de Referência"
              initialValue={0}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default NfsDeductionModalForm;
